import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { KPIIcon } from '~/modules/common/components/Icons';
import { ChartCard } from '~/modules/common/charts/dashboard';
import { useIsBreakpointDown, useScrollToTop } from '~/modules/common/hooks';
import ErrorBoundary from '~/modules/common/components/ErrorBoundary';
import { useHasPermission } from '~/modules/common/permissions';
import { useMeContext } from '~/modules/me/useMeContext';
import ProjectCard from './ProjectCard';
import ActiveTasksCard from './ActiveTasksCard';
import ResourcesCard from './ResourcesCard';
import ProjectsKPI from './ProjectsKPI';

const useStyles = makeStyles(theme => ({
  container: {
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1)
    },
    minHeight: '-webkit-max-content'
  },
  item: {
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1)
    },
    [theme.breakpoints.down('xs')]: {
      paddingTop: theme.spacing(2)
    }
  }
}));

const icons = {
  overview: <KPIIcon />
};

export const ProjectsOverview = () => {
  useScrollToTop();
  const classes = useStyles();
  const {
    hasViewProjectCostType,
    hasEditCompletedResourceAllocation,
    featureFlags: { isPsaPrpFixCardPermissionEnabled }
  } = useMeContext();

  const canViewTasks = useHasPermission({
    actionUri: 'urn:replicon:project-action:view-tasks'
  });

  const canViewCostData = useHasPermission({
    actionUri: 'urn:replicon:project-action:view-cost-data'
  });

  const canViewBillingData = useHasPermission({
    actionUri: 'urn:replicon:project-action:view-billing-data'
  });

  const isMobile = useIsBreakpointDown('xs');

  const labels = {
    overview: isMobile ? (
      <FormattedMessage id="projectOverview.kpi" />
    ) : (
      <FormattedMessage id="projectOverview.keyPerformanceIndicators" />
    )
  };

  const shouldShowKPICard = isPsaPrpFixCardPermissionEnabled
    ? canViewCostData || canViewBillingData || hasViewProjectCostType
    : true;

  const cardLgWidth = hasEditCompletedResourceAllocation ? 4 : 6;

  return (
    <Grid spacing={0} container className={classes.container}>
      {shouldShowKPICard && (
        <Grid className={classes.item} item xs={12} sm={12} lg={12}>
          <ErrorBoundary key="ProjectsOverviewChart">
            <ChartCard
              chartTitle={labels.overview}
              chartAvatar={icons.overview}
              ChartComponent={ProjectsKPI}
              showScaleSelector
              showPeriodFilter
            />
          </ErrorBoundary>
        </Grid>
      )}
      <Grid className={classes.item} item xs={12} sm={6} lg={cardLgWidth}>
        <ProjectCard />
      </Grid>
      {canViewTasks && (
        <Grid className={classes.item} item xs={12} sm={6} lg={cardLgWidth}>
          <ActiveTasksCard />
        </Grid>
      )}
      {hasEditCompletedResourceAllocation && (
        <Grid className={classes.item} item xs={12} sm={6} lg={cardLgWidth}>
          <ResourcesCard />
        </Grid>
      )}
    </Grid>
  );
};

export default ProjectsOverview;
